import { UndoOutlined } from "@ant-design/icons"
import { Alert } from "components/Alert/Alert"
import { BackButton } from "components/BackButton/BackButton"
import { Button, type ButtonProps } from "components/Button/Button"
import { Drawer } from "components/Drawer/Drawer"
import { useDisclosure } from "hooks/useDisclosure"
import { useMemo } from "react"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { DeviceSwUpdate } from "./DeviceSwUpdate"
import { Feedback } from "./Feedback"
import { SelectWifi } from "./SelectWifi"
import { useFootprint } from "./useFootprint"

export function Configuration(props: ButtonProps) {
	const footprint = useFootprint()
	const config = useDisclosure()
	const error = useMemo(() => {
		if (!footprint.isWebUsbAvailable) {
			return {
				show: true,
				message: "Unsupported devices",
				description:
					"Your browser does not support WebUSB. Please use Chrome on a desktop or Android device.",
			}
		}
		return {
			show: footprint.hasError,
			message: "Footprint connection error",
			description: footprint.message as string,
		}
	}, [footprint])
	const deviceName =
		footprint.model && footprint.serialNumber
			? `${footprint.model}-${footprint.serialNumber}`
			: DEFAULT_PLACEHOLDER
	const handleOpen = async () => {
		config.open()
		await footprint.requestAuthorizedDevice()
	}
	const handleResetConfig = async () => {
		await footprint.requestAuthorizedDevice()
	}

	return (
		<>
			<Button {...props} type="primary" onClick={handleOpen}>
				Footprint configuration
			</Button>

			<Drawer
				open={config.isOpen}
				mask={{ closable: false }}
				onClose={config.close}
				title={<BackButton onClick={config.close}>Start visit</BackButton>}
				aria-label="Footprint configuration"
			>
				<Feedback
					spinning={!!footprint.executing.DATAREAD}
					className="bg-transparent"
				>
					<div className="flex flex-col gap-6 overflow-y-auto">
						<h2 className="flex flex-wrap items-center justify-between gap-1 font-headings text-xl font-semibold sm:text-2xl">
							{deviceName}
							{footprint.isWebUsbAvailable && (
								<Button
									danger
									icon={<UndoOutlined aria-hidden />}
									onClick={handleResetConfig}
								>
									Reset
								</Button>
							)}
						</h2>

						{error.show && (
							<Alert
								closable
								showIcon
								type="error"
								banner={false}
								title={error.message}
								description={error.description}
								onClose={() => footprint.clearError()}
							/>
						)}

						{footprint.isWebUsbAvailable && (
							<>
								<SelectWifi />

								<DeviceSwUpdate />
							</>
						)}
					</div>
				</Feedback>
			</Drawer>
		</>
	)
}
